import { create } from 'zustand'
import { api } from '../lib/api.js'

const empty = () => ({
  demographics: {
    alter: '', organisation: '', position: '', bereich: '', branche: '',
    fuehrungsverantwortung: '', bildungsabschluss: '', fachrichtung: '',
  },
  motivators: { selected: [], definitions: {}, additional: ['', '', '', '', ''] },
  demotivators: { selected: [], definitions: {}, additional: ['', '', '', '', ''] },
})

export const useSurvey = create((set, get) => ({
  accessId: null,
  status: 'idle',     // idle | loaded | saving | completed
  step: 0,            // 0 = welcome
  data: empty(),
  error: null,

  async load(accessId) {
    set({ accessId, status: 'idle', error: null })
    try {
      const r = await api.getResponse(accessId)
      set({
        step: r?.current_step ?? 0,
        data: r?.data && Object.keys(r.data).length ? { ...empty(), ...r.data } : empty(),
        status: r?.status === 'completed' ? 'completed' : 'loaded',
      })
    } catch (e) {
      set({ error: e.message, status: 'loaded' })
    }
  },

  setStep(step) { set({ step }) },

  setDemographics(patch) {
    set(s => ({ data: { ...s.data, demographics: { ...s.data.demographics, ...patch } } }))
  },

  toggleSelected(kind, label, max = 10) {
    set(s => {
      const cur = s.data[kind].selected
      const next = cur.includes(label) ? cur.filter(x => x !== label)
                  : cur.length >= max ? cur
                  : [...cur, label]
      return { data: { ...s.data, [kind]: { ...s.data[kind], selected: next } } }
    })
  },

  setDefinition(kind, word, prioIdx, defIdx) {
    // prioIdx: 0 = Priorität 1, 1 = Priorität 2
    set(s => {
      const defs = { ...(s.data[kind].definitions || {}) }
      const cur = defs[word] ? [...defs[word]] : [null, null]
      // wenn defIdx schon irgendwo gesetzt, entfernen (verhindert Doppelt-Auswahl)
      cur.forEach((v, i) => { if (v === defIdx) cur[i] = null })
      cur[prioIdx] = defIdx
      defs[word] = cur
      return { data: { ...s.data, [kind]: { ...s.data[kind], definitions: defs } } }
    })
  },

  removeDefinition(kind, word, prioIdx) {
    set(s => {
      const defs = { ...(s.data[kind].definitions || {}) }
      const cur = defs[word] ? [...defs[word]] : [null, null]
      cur[prioIdx] = null
      defs[word] = cur
      return { data: { ...s.data, [kind]: { ...s.data[kind], definitions: defs } } }
    })
  },

  setAdditional(kind, idx, value) {
    set(s => {
      const additional = [...s.data[kind].additional]
      additional[idx] = value
      return { data: { ...s.data, [kind]: { ...s.data[kind], additional } } }
    })
  },

  // Generic key/value setter for surveys that store flat answers (e.g. MyCulture)
  setAnswer(key, value) {
    set(s => ({ data: { ...s.data, answers: { ...(s.data.answers || {}), [key]: value } } }))
  },

  async save() {
    const { accessId, step, data } = get()
    if (!accessId) return
    set({ status: 'saving' })
    try {
      await api.saveResponse(accessId, { currentStep: step, data })
      set({ status: 'loaded' })
    } catch (e) {
      set({ status: 'loaded', error: e.message })
    }
  },

  async finish() {
    const { accessId, data } = get()
    await api.finishResponse(accessId, { data })
    set({ status: 'completed' })
  },

  reset() { set({ accessId: null, status: 'idle', step: 0, data: empty(), error: null }) },
}))
